import { ChevronDown, Radio, TrendingUp, TrendingDown, X } from "lucide-react";
import { DemoHint } from "@/components/demo/DemoHint";
import { useState, useEffect, useRef, useCallback } from "react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface FeedItem {
  id: string;
  type: "offer" | "alert" | "update" | "match" | "rate";
  text: string;
  timestamp: number;
  trackedLaneId?: string;
  percentChange?: number;
  laneLabel?: string;
  rateStr?: string;
  pctStr?: string;
  countStr?: string;
}

const STATE_ABBR: Record<string, string> = {
  'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR', 'california': 'CA',
  'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE', 'florida': 'FL', 'georgia': 'GA',
  'hawaii': 'HI', 'idaho': 'ID', 'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA',
  'kansas': 'KS', 'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
  'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS', 'missouri': 'MO',
  'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ',
  'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH',
  'oklahoma': 'OK', 'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT', 'vermont': 'VT',
  'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV', 'wisconsin': 'WI', 'wyoming': 'WY',
};

function extractStateAbbr(location: string): string {
  const raw = (location || '').trim();
  if (raw.length === 2 && /^[a-zA-Z]{2}$/.test(raw)) return raw.toUpperCase();
  const parts = raw.split(',').map(p => p.trim()).filter(Boolean);
  for (const part of parts) {
    if (STATE_ABBR[part.toLowerCase()]) return STATE_ABBR[part.toLowerCase()];
    const token = part.split(' ')[0]?.trim() || '';
    if (token.length === 2 && /^[a-zA-Z]{2}$/.test(token)) return token.toUpperCase();
  }
  const letters = (parts[0] || raw).replace(/[^a-zA-Z]/g, '');
  return letters.length >= 2 ? letters.slice(0, 2).toUpperCase() : raw.toUpperCase();
}

export function LiveFeedV2() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [items, setItems] = useState<FeedItem[]>([]);
  const idCounter = useRef(0);

  const addItem = useCallback((type: FeedItem["type"], text: string) => {
    idCounter.current += 1;
    const item: FeedItem = { id: `feed-${idCounter.current}`, type, text, timestamp: Date.now() };
    setItems(prev => [item, ...prev].slice(0, 30)); // keep last 30
  }, []);

  // Seed with recent activity on mount
  useEffect(() => {
    if (!user) return;

    const loadRecent = async () => {
      // Recent offers on user's loads
      const { data: recentOffers } = await supabase
        .from('offers')
        .select(`
          id, rate_amount, status, created_at,
          loads!inner(load_number, origin_city, destination_city, created_by)
        `)
        .order('created_at', { ascending: false })
        .limit(5);

      const userOffers = (recentOffers || []).filter((o: any) => o.loads?.created_by === user.id);
      userOffers.forEach((o: any) => {
        const lane = `${o.loads.origin_city} → ${o.loads.destination_city}`;
        if (o.status === 'countered') {
          addItem('update', `${o.loads.load_number} counter-offered at $${o.rate_amount?.toLocaleString()} — awaiting response`);
        } else {
          addItem('offer', `New offer $${o.rate_amount?.toLocaleString()} on ${lane}`);
        }
      });

      // Recent rate alert matches
      const { data: alertMatches } = await supabase
        .from('lane_alert_matches')
        .select(`
          id, matched_at,
          rate_alerts!inner(origin, destination, user_id),
          loads!inner(load_number, rate_amount)
        `)
        .order('matched_at', { ascending: false })
        .limit(5);

      const userAlerts = (alertMatches || []).filter((m: any) => m.rate_alerts?.user_id === user.id);
      userAlerts.forEach((m: any) => {
        const lane = `${m.rate_alerts.origin} → ${m.rate_alerts.destination}`;
        addItem('alert', `Rate alert match • ${lane} — ${m.loads.load_number} at $${m.loads.rate_amount?.toLocaleString()}`);
      });

      // If no real events, show a welcome item
      if (userOffers.length === 0 && userAlerts.length === 0) {
        addItem('match', 'Live feed active — new offers and alerts will appear here');
      }
    };

    loadRecent();
  }, [user, addItem]);

  // Fetch tracked lanes and add as rate items
  const fetchTrackedLanes = useCallback(async () => {
    if (!user?.id) return;
    const { data: lanes } = await supabase
      .from('tracked_lanes')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (!lanes || lanes.length === 0) return;

    // Get rate data from ticker view
    const { data: tickerData } = await supabase
      .from('lane_rate_ticker_view')
      .select('*');

    const rateMap = new Map<string, { avg: number; pct: number; count: number }>();
    for (const r of tickerData || []) {
      rateMap.set(`${(r.origin_state || '').toUpperCase()}→${(r.destination_state || '').toUpperCase()}`, {
        avg: Number(r.avg_rate_current) || 0,
        pct: Number(r.percent_change) || 0,
        count: Number(r.load_count) || 0,
      });
    }

    // Remove old rate items, then add fresh ones
    setItems(prev => {
      const nonRate = prev.filter(i => i.type !== 'rate');
      const rateItems: FeedItem[] = lanes.map((lane, idx) => {
        const oAbbr = extractStateAbbr(lane.origin);
        const dAbbr = extractStateAbbr(lane.destination);
        const key = `${oAbbr}→${dAbbr}`;
        const stats = rateMap.get(key);
        const rateStr = stats && stats.avg > 0 ? `$${Math.round(stats.avg).toLocaleString()}` : 'No data';
        const pct = stats?.pct || 0;
        const pctStr = pct !== 0 ? `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%` : '';
        const countStr = stats && stats.count > 0 ? `${stats.count} loads` : '';
        const eqLabel = lane.equipment_type ? ` (${lane.equipment_type})` : '';
        const laneLabel = `${oAbbr} → ${dAbbr}${eqLabel}`;
        return {
          id: `rate-${lane.id}-${idx}`,
          type: 'rate' as const,
          text: `${laneLabel} ${rateStr} ${pctStr} ${countStr}`.trim(),
          timestamp: Date.now(),
          trackedLaneId: lane.id,
          percentChange: pct,
          laneLabel,
          rateStr,
          pctStr,
          countStr,
        };
      });
      return [...rateItems, ...nonRate].slice(0, 30);
    });
  }, [user?.id]);

  useEffect(() => {
    fetchTrackedLanes();
    const h1 = () => fetchTrackedLanes();
    window.addEventListener('tracked-lane-added', h1);
    window.addEventListener('trackedLaneDeleted', h1);
    return () => {
      window.removeEventListener('tracked-lane-added', h1);
      window.removeEventListener('trackedLaneDeleted', h1);
    };
  }, [fetchTrackedLanes]);

  // Real-time subscriptions
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel('live-feed')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'offers' }, (payload) => {
        const o = payload.new as any;
        addItem('offer', `New offer received — $${o.rate_amount?.toLocaleString()}`);
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'offers' }, (payload) => {
        const o = payload.new as any;
        if (o.status === 'countered') {
          addItem('update', `Offer countered at $${o.rate_amount?.toLocaleString()}`);
        } else if (o.status === 'accepted') {
          addItem('match', `Offer accepted — load awarded`);
        }
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'loads' }, (payload) => {
        const l = payload.new as any;
        if (l.status === 'booked') {
          addItem('match', `${l.load_number} booked — carrier assigned`);
        }
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'lane_alert_matches' }, () => {
        addItem('alert', 'New rate alert match detected');
      })
      .subscribe((status) => {
        // Self-heal: pull tracked lanes on (re)connect so any lane state
        // that changed during a CHANNEL_ERROR gap is reflected in the feed.
        if (status === 'SUBSCRIBED') fetchTrackedLanes();
      });

    // Focus / visibility self-heal.
    let focusTimer: ReturnType<typeof setTimeout> | null = null;
    const runDebounced = () => {
      if (focusTimer) return;
      focusTimer = setTimeout(() => { focusTimer = null; fetchTrackedLanes(); }, 500);
    };
    const onFocus = () => runDebounced();
    const onVis = () => { if (document.visibilityState === 'visible') runDebounced(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);

    return () => {
      supabase.removeChannel(channel);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
      if (focusTimer) clearTimeout(focusTimer);
    };
  }, [user, addItem, fetchTrackedLanes]);

  const handleUnpin = async (item: FeedItem) => {
    if (!item.trackedLaneId || !user?.id) return;
    const { error } = await supabase
      .from('tracked_lanes')
      .delete()
      .eq('id', item.trackedLaneId)
      .eq('user_id', user.id);
    if (error) {
      console.error('Unpin failed:', error.message);
      toast.error('Failed to unpin lane');
      return;
    }
    toast.success('Lane unpinned');
    window.dispatchEvent(new Event('trackedLaneDeleted'));
  };

  if (dismissed) return null;

  // Double items for seamless scroll loop
  const displayItems = items.length > 0 ? [...items, ...items] : [];

  return (
    <div className="flex items-center gap-0 rounded-lg border bg-card overflow-hidden h-10">
      <div className="flex h-full flex-shrink-0 items-center gap-2 border-r bg-muted/40 px-3">
        <Radio className={cn("h-3 w-3", items.length > 0 ? "text-success" : "text-muted-foreground")} />
        <DemoHint hintKey="section.liveFeed" side="right">
          <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground whitespace-nowrap">Live Feed</span>
        </DemoHint>
      </div>
      <div className="flex-1 overflow-hidden relative h-full flex items-center">
        {displayItems.length > 0 ? (
          <div className="flex animate-ticker gap-10 whitespace-nowrap" style={{ width: "max-content" }}>
            {displayItems.map((item, i) => (
              <span key={`${item.id}-${i}`} className="text-caption text-foreground flex-shrink-0 flex items-center gap-2">
                {item.type === "rate" ? (
                  <>
                    {(item.percentChange ?? 0) > 0 ? (
                      <TrendingUp className="h-3 w-3 text-success flex-shrink-0" />
                    ) : (item.percentChange ?? 0) < 0 ? (
                      <TrendingDown className="h-3 w-3 text-destructive flex-shrink-0" />
                    ) : (
                      <span className="h-1.5 w-1.5 rounded-full flex-shrink-0 inline-block bg-muted-foreground" />
                    )}
                    <span>{item.laneLabel}</span>
                    <span className={cn(
                      "font-semibold",
                      (item.percentChange ?? 0) > 0 && "text-success",
                      (item.percentChange ?? 0) < 0 && "text-destructive",
                      (item.percentChange ?? 0) === 0 && "text-muted-foreground",
                    )}>{item.rateStr}</span>
                    {item.pctStr && (
                      <span className={cn(
                        "font-semibold",
                        (item.percentChange ?? 0) > 0 && "text-success",
                        (item.percentChange ?? 0) < 0 && "text-destructive",
                      )}>{item.pctStr}</span>
                    )}
                    {item.countStr && (
                      <span className="text-muted-foreground">• {item.countStr}</span>
                    )}
                    {item.trackedLaneId && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleUnpin(item); }}
                        className="rounded-full p-0.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                        title="Unpin lane"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <span className={cn(
                      "h-1.5 w-1.5 rounded-full flex-shrink-0 inline-block",
                      item.type === "alert"  && "bg-warning",
                      item.type === "offer"  && "bg-accent",
                      item.type === "match"  && "bg-success",
                      item.type === "update" && "bg-muted-foreground",
                    )} />
                    {item.text}
                  </>
                )}
              </span>
            ))}
          </div>
        ) : (
          <span className="text-caption text-muted-foreground px-3">No recent activity</span>
        )}
      </div>
      <div className="flex items-center gap-0 border-l flex-shrink-0">
        <button
          onClick={() => setItems([])}
          className="flex items-center gap-1.5 h-full px-3 text-[10px] font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <span className="h-3 w-3 rounded-sm bg-accent/20 border border-accent/40 flex items-center justify-center">
            <span className="h-1.5 w-1.5 rounded-sm bg-accent" />
          </span>
          Dismiss All
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="flex h-full items-center px-2 border-l text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          <ChevronDown className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
